import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, Envelope, Plug } from "@phosphor-icons/react";

const EMPTY = {
  name: "", imap_host: "", imap_port: 993, imap_user: "", imap_password: "",
  imap_use_ssl: true, imap_mailbox: "INBOX", client_id: "", enabled: true,
};

export default function Mailboxes() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([api.get("/mailboxes"), api.get("/clients")]);
      setItems(m.data); setClients(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (mb) => {
    setForm({ ...EMPTY, ...mb, client_id: mb.client_id || "" });
    setEditing(mb); setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        imap_port: Number(form.imap_port) || 993,
      };
      if (editing === "new") await api.post("/mailboxes", payload);
      else await api.put(`/mailboxes/${editing.id}`, payload);
      setEditing(null);
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete mailbox?")) return;
    await api.delete(`/mailboxes/${id}`); await load();
  };

  const test = async (id) => {
    setTesting(t => ({ ...t, [id]: true }));
    try {
      const { data } = await api.post(`/mailboxes/${id}/test`);
      setTestResult(r => ({ ...r, [id]: data }));
    } catch (e) {
      setTestResult(r => ({ ...r, [id]: { ok: false, message: formatApiError(e.response?.data?.detail) || e.message } }));
    } finally {
      setTesting(t => ({ ...t, [id]: false }));
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  return (
    <Layout>
      <PageHeader
        title="Inbound mailboxes"
        subtitle="Per-vendor or per-client IMAP mailboxes. Use when the vendor has no API."
        actions={
          <button onClick={openNew} data-testid="mailboxes-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New mailbox
          </button>
        }
      />
      <div className="p-6">
        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Envelope size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No mailboxes yet</h3>
            <p className="text-sm text-slate-400 mb-4">
              Add one or many inbound mailboxes — useful when a vendor has no API and only sends reports via email.
            </p>
            <button onClick={openNew} data-testid="mailboxes-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add mailbox
            </button>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="mailboxes-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Host</th>
                  <th className="text-left py-3 px-4">User</th>
                  <th className="text-left py-3 px-4">Client</th>
                  <th className="text-left py-3 px-4">Last poll</th>
                  <th className="text-left py-3 px-4">Enabled</th>
                  <th className="text-right py-3 px-4 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((mb) => {
                  const tr = testResult[mb.id];
                  return (
                    <tr key={mb.id} className="border-t border-border hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{mb.name}</td>
                      <td className="py-3 px-4 text-xs text-slate-300">{mb.imap_host}:{mb.imap_port}</td>
                      <td className="py-3 px-4 text-xs text-slate-300">{mb.imap_user}</td>
                      <td className="py-3 px-4 text-slate-300">{clientName(mb.client_id)}</td>
                      <td className="py-3 px-4 text-xs">
                        {mb.last_poll_status ? (
                          <span className={mb.last_poll_status.startsWith("error") ? "text-red-400" : "text-emerald-400"}>
                            {mb.last_poll_status}
                          </span>
                        ) : <span className="text-slate-500">never</span>}
                      </td>
                      <td className="py-3 px-4 text-xs">
                        <span className={mb.enabled ? "text-emerald-400" : "text-slate-500"}>{mb.enabled ? "ON" : "OFF"}</span>
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button onClick={() => test(mb.id)} disabled={testing[mb.id]} data-testid={`mailbox-test-${mb.id}`}
                          className={`text-xs px-2 py-1 mr-1 rounded border ${tr ? (tr.ok ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400") : "border-border text-slate-300 hover:bg-white/5"}`}
                          title={tr?.message || "Test IMAP connection"}>
                          {testing[mb.id] ? "…" : tr ? (tr.ok ? "OK" : "FAIL") : "Test"}
                        </button>
                        <button onClick={() => openEdit(mb)} data-testid={`mailbox-edit-${mb.id}`}
                          className="p-1.5 text-slate-300 hover:text-white"><PencilSimple size={14} /></button>
                        <button onClick={() => remove(mb.id)} data-testid={`mailbox-delete-${mb.id}`}
                          className="p-1.5 text-slate-300 hover:text-red-400 ml-1"><Trash size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="mailbox-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New mailbox" : "Edit mailbox"}</h3>
            <FieldM label="Name" required>
              <input required value={form.name} placeholder="e.g. Veeam reports" onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mailbox-name-input" />
            </FieldM>
            <div className="grid grid-cols-2 gap-3">
              <FieldM label="IMAP Host" required>
                <input required value={form.imap_host} placeholder="imap.example.com" onChange={(e) => setForm({ ...form, imap_host: e.target.value })} data-testid="mailbox-host-input" />
              </FieldM>
              <FieldM label="Port">
                <input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: e.target.value })} data-testid="mailbox-port-input" />
              </FieldM>
              <FieldM label="Username" required>
                <input required value={form.imap_user} onChange={(e) => setForm({ ...form, imap_user: e.target.value })} data-testid="mailbox-user-input" />
              </FieldM>
              <FieldM label="Password" required={editing === "new"}>
                <input type="password" required={editing === "new"} value={form.imap_password} placeholder={editing === "new" ? "" : "leave blank to keep"} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} data-testid="mailbox-password-input" />
              </FieldM>
              <FieldM label="Mailbox folder">
                <input value={form.imap_mailbox} onChange={(e) => setForm({ ...form, imap_mailbox: e.target.value })} />
              </FieldM>
              <FieldM label="Client">
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="mailbox-client-select">
                  <option value="">(none — global)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FieldM>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" checked={form.imap_use_ssl} onChange={(e) => setForm({ ...form, imap_use_ssl: e.target.checked })} />
              Use SSL/TLS
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="mailbox-enabled-checkbox" />
              Enabled (will be polled)
            </label>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setEditing(null)}
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="mailbox-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function FieldM({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
